import { Link } from "@mui/material";

import {
  faqs,
  howItWorks,
  AnalyticsHeader,
  purchaseSeatsLanguages,
} from "./helpers";
import { routes } from "@/global/routes";
import { vendorUUIDs } from "../../constants";
import { DefaultCheckoutTerms, DefaultRegistrationTerms } from "../../default_terms_and_conditions";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

import StaticSeatMap from "public/images/albuquerqueisotopes/2025_Attend_AllStarFamilyPlan_Map.png";
import FindGamesBanner from "public/images/albuquerqueisotopes/Attend_Family4Pack_Banner_1486x146.png";

const config: TeamConfig = {
  teamSchema: "albuquerqueisotopes_flexpack",
  browserTitle: "All-Star Family Plan",
  packages: true,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "", // Optional, set to empty string
  teamName: "Albuquerque Isotopes",

  // Seat map configs
  seatMapConfigs: {
    venueId: "nam-us-30388-baseball-sec",
    mapId: "blockmap2d",
    staticSeatMapImage: StaticSeatMap.src,
    customSeatMapStaticImageStyle: {
      width: "62%",
      display: "block",
      marginX: "auto",
    },
    isView3dButtonHidden: true,
  },

  // Gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: "",
    redeemSuccessModalLink: "",
    hideRedeemItem: false,
    customShareGiftUrl: "",
    customErrorMessage: "",
    hideRecipient: false,
  },

  // Purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // Disabled routes
  disabledRoutes: [routes.giftCard],

  // Switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // Purchase seat configs
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 14,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
    isDisabledEventSelection: false,
    hideAllUpcomingGames: false,
    isRepProduct: false,
  },

  // Feature configs
  packageConfigs: {
    isPackageEnabled: true,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
    cantSelectMoreGames: true,
  },

  // Intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // Checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  oktaIssuer: "",
  oktaClientId: "",
  isSaveCardAllowed: true,
  shouldUseInternalLogin: false,
  clientId: "07ddf826a301.seasonshare.isotopes-isotopes.us",
  platform: vendorUUIDs.ticketmaster,
  refreshInventoryWhenEventClick: false,

  // Analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "669509049385777",
    trackMetaPixelOnCheckout: true,
    AnalyticsHeader,
  },

  // Redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // URLs
  accountManagerUrl: "https://am.ticketmaster.com/isotopes/",

  // Images
  images: {
    findGamesBannerImage: FindGamesBanner.src,
    authBackgroundImage: FindGamesBanner.src,
    checkoutStatusPageImage: FindGamesBanner.src,
    logoImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/Albuquerque-isotopes-primary.png" +
      "?width=128&height=75", // actual size 260x224
    logoNameImage: "",
    faviconImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/Albuquerque-isotopes-primary.png",
    seatMap3dPreviewImage: "",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },
    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },
    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },
    progressBar: {
      staticProgressBarText: false,
    },
    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:5059242255"
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          (505) 924-2255
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#D31144",
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "",
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: purchaseSeatsLanguages,
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your Albuquerque Isotopes tickets are confirmed!",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },

        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
